<template>
    <div class="praise-modal__compose">
        <p v-if="responses.length > 1" class="select-response-message">
            Select response to highlight
        </p>
        <PraiseModalResponseCarousel
            :responses="responses"
            :is-mobile="isMobile"
            :show-carousel-indicators="showCarouselIndicators"
            @praise-change="onPraiseChange"
        />

        <div class="praise-modal__text-input">
            <AtTa
                :members="members"
                name-key="name"
                :ats="['@', '#']"
                :filter-match="atFilterMatch"
                :delete-match="atDeleteMatch"
            >
                <template #item="scope">
                    <span
                        class="name"
                        data-testid="fm-dash-at-name"
                        v-text="scope.item.name"
                    />
                    <span
                        class="email"
                        v-text="
                            scope.item.email !== undefined
                                ? scope.item.email
                                : scope.item.description
                        "
                    />
                </template>
                <Textarea
                    v-model="textInput"
                    class="praise-modal__text-input__text-area"
                    rows="5"
                    size="large"
                    :placeholder="inputPlaceholder"
                />
            </AtTa>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import AtTa from 'vue-at/dist/vue-at-textarea'
import Textarea from 'primevue/textarea'
import PraiseModalResponseCarousel from '@/components/FrontlineManagerDash/PraiseModal/PraiseModalResponseCarousel.vue'
import { ActionResponse, TeamMember } from '@/entities/frontlineManagerDash'

interface Props {
    responses: ActionResponse[]
    teamMembers: TeamMember[]
    tags: string[]
    inputPlaceholder: string
    showCarouselIndicators: boolean
    isMobile?: boolean
}
const props = withDefaults(defineProps<Props>(), {
    showCarouselIndicators: true,
    isMobile: false,
})

const emit = defineEmits(['responseChanged'])

const textInput = defineModel('textInput', {
    default: '',
    type: String,
})

const onPraiseChange = (response: ActionResponse) => {
    emit('responseChanged', response)
}

const members = computed(() => {
    const validTeamMembers =
        props.teamMembers?.filter((member) => member?.name) ?? []
    const validTags = props.tags?.map((tag) => ({ name: tag, email: '' })) ?? []

    return [...validTeamMembers, ...validTags].sort((a, b) =>
        a.name.localeCompare(b.name)
    )
})

const atFilterMatch = (name, chunk, at) => {
    if (!name || !at) {
        return false
    }

    const isNameMatchingChunk =
        name?.toLowerCase()?.indexOf(chunk?.toLowerCase() ?? '') === 0
    const isMentionMatch =
        at === '@' &&
        props.teamMembers.length &&
        props.teamMembers.some((mention) => mention.name === name)
    const isTagMatch = at === '#' && props.tags.includes(name)

    return isNameMatchingChunk && (isMentionMatch || isTagMatch)
}

const atDeleteMatch = (name, chunk, suffix) => {
    if (!name || !chunk || !suffix) {
        return false
    }

    return chunk === name + suffix
}
</script>

<style lang="less" scoped>
@import '@/styles/prime-vue/variables.less';
@import '@/styles/prime-vue/colours.less';
@import '@/styles/rain/variables.less';

.select-response-message {
    color: @text-tertiary;
    font-size: @fontSize-base;
    line-height: @lineHeight-base;
    margin-bottom: @spacing-lg;
}

.praise-modal__text-input {
    margin-bottom: 24px;

    &__text-area {
        font-size: @fontSize-md;
        box-sizing: border-box;
        width: 100%;
        max-width: 100%;
        min-width: 100%;
        max-height: 300px;
    }
}

.atwho-view {
    max-width: 350px !important;
    bottom: 0.6rem;

    .email {
        font-size: 11px;
        color: #8097b1;
    }
    .name {
        flex: 1;
        margin-right: 5px;
    }
}
</style>
